import {
  BookingField,
  BookingFieldGroup,
  DynamicHash,
  PriceData,
  SearchField,
  SearchFilter,
  SelectedFilters,
  Tariff,
  DropDownFieldValueObject,
  fieldVisibility,
} from "types";
import { createMetaData, createMetaDataLink } from "@/utils/documentMetadata";
import { applyLanguage, applyThemeClasses } from "@/utils/appTheme";
import {
  affiliateIdToUse,
  decodeBase64,
  isBase64,
  isBase64EncodedAffiliatePayload,
} from "@/utils/affiliateId";
import {
  comparisonValueTitle,
  decodeHtmlEntities,
  isHtmlEmpty,
} from "@/utils/htmlContent";
import {
  collectionLabelsEqual,
  findSingleExactCollectionOptionByLabel,
  isResolvedCollectionValue,
  normalizeCountryCollectionValue,
  sortDropDownOptions,
} from "@/utils/collectionValues";
import {
  iFrameResize,
  postMessageWrapper,
  iFrameResizeNow,
  iFrameResizeToPixels,
} from "@/utils/iframeCommunication";
import { sessionStorageGet, sessionStorageSet } from "@/utils/sessionStorage";
import {
  getCookie,
  getCookieValue,
  hasCookie,
  setCookie,
} from "@/utils/cookieStorage";
import {
  checkForRelativeDates,
  formatDate,
  getDefaultDate,
  calculateLegalAge,
  isValidDate,
  isValidDateFormat,
  setValidDate,
  swapDateFormats,
} from "@/utils/dateFormatting";

import {
  getPriceChangeState,
  getSanitizedNumber,
  normalizeNumberString,
  normalizeNumberStringForUrl,
  parsePriceToNumber,
  tariffPrice,
  withDotDecimalsForNumberFields,
  priceFormat,
} from "@/utils/numberFormatting";
import {
  asyncfocusDatePicker,
  asyncfocusMultiselect,
  resetMultiselectDropdown,
  resizeMultiselectDropdown,
} from "@/utils/multiselectDom";

import type { Router } from "vue-router";
import type { Ref } from "vue";
import { useSearchStore } from "@/store/searchModule";
import { useRootStore } from "@/store/root";
import debounce from "lodash.debounce";
import { useBookingStore } from "@/store/bookingModule";
import type { useTariffStore } from "@/store/tariffModule";
import { updatePriceEndPoint } from "@/apiCalls/updatePriceEndPoint";
import { updateTariffPrice } from "@/composables/booking/updateTariffPrice";
import { readApplyPageFieldBoolean } from "@/composables/booking/bookingFieldGroupApplyPages";
import { conditionsForBookingFieldEval } from "../composables/booking/bookingFieldConditions";
import { buildSearchFiltersForContractPayload } from "@/utils/searchFiltersNormalize";
import {
  getVisibleInsuredEntityFieldNames,
  isEmptyInsuredEntityRecord,
} from "@/utils/insuredEntityPayload";
import { rollbar } from "../../rollbar";
import { isRequestCanceled } from "@/utils/isRequestCanceled";
import {
  enforceEmailVerificationInPolicyHolder,
  isEmailVerificationEnforced,
} from "@/utils/emailVerificationConfig";

type BookingStore = ReturnType<typeof useBookingStore>;
type RootStore = ReturnType<typeof useRootStore>;
type SearchStore = ReturnType<typeof useSearchStore>;
type TariffStore = ReturnType<typeof useTariffStore>;

type SearchFilterRow = SearchFilter & {
  search_field_id?: string | number;
  tooltip?: string | string[];
  display_value?: string;
  field_name?: string;
};

type CollectionFieldValue = DropDownFieldValueObject & {
  name?: string;
};

type BookingFormSlice = Record<string, unknown>;

type QueryValueItem = {
  type: string;
  value: unknown;
};

type BookingFieldsContractState = {
  visibleBookingFields: Record<string, Record<string, fieldVisibility>>;
  bookingData: {
    bookingForms: BookingFieldGroup[];
    chosenPaymentMethod: string;
    acceptMarketingAffiliate?: boolean;
    sortedBookingForms?: BookingFieldGroup[];
    verificationLevel?: unknown;
    $state: Record<string, BookingFormSlice> & {
      policy_holder: BookingFormSlice;
      insured_entity: Record<string, DynamicHash[]>;
      payment_method: BookingFormSlice;
      bookingSession?: { booking_fields?: Record<string, BookingFormSlice> };
    };
  };
};

type TariffContractData = {
  affiliateData: Record<string, unknown>;
  code: unknown;
  priceToken: string;
  campaign: string;
  completed_by?: string;
  customer?: string;
  app: string;
  tariff: Tariff;
  visibleBookingFields?: BookingFieldsContractState["visibleBookingFields"];
  bookingData: BookingFieldsContractState["bookingData"] & {
    insuredEntityPriceArray: number[];
    currentTariffPrice: string | number;
    selectedAddons?: string[];
    policy_holder: Record<string, unknown>;
    visibleBookingFields: VisibleBookingFieldsMap;
    verificationLevel?: unknown;
  };
  searchFilters: SearchFilterRow[];
  searchForm?: SearchField[];
  originalSearchMask: unknown;
  missingFilters: SearchFilter[][];
  linkoutVisibleFields?: Record<
    string,
    { id: string | number; name: string; value: unknown }
  >;
} & BrokerAffiliateContractSlice;

type LinkoutContractData = {
  affiliateData: Record<string, unknown>;
  code?: unknown;
  priceToken?: string;
  campaign: string;
  customer?: string;
  app: string;
  tariff: Tariff;
  searchFilters: SearchFilterRow[];
  originalSearchMask: unknown;
  linkoutVisibleFields?: Record<
    string,
    { id: string | number; name: string; value: unknown }
  >;
} & BrokerAffiliateContractSlice;

type SearchFieldAttributesWithFormatting = SearchField["attributes"] & {
  precision?: number | string | null;
  thousand_separator?: string | null;
  unit?: string;
};

type CollectionValueBookingStore = {
  $state: Record<string, unknown> & {
    insured_entity?: Record<string, Record<string, unknown>[]>;
    policy_holder?: Record<string, unknown>;
  };
};

type VisibleBookingFieldsMap = Record<
  string,
  Record<string, fieldVisibility> | Array<Record<string, fieldVisibility>>
>;

type EntityPriceDataEntry = {
  form_name: string;
  fields: DynamicHash[];
};

type UpdatePriceCallBackFn = (
  appName: string,
  payload: DynamicHash,
) => Promise<unknown>;

type CreateUpdatePriceCallBackParams = {
  router: Router;
  updatePriceEndPoint: typeof updatePriceEndPoint;
  tariffRef: Ref<Tariff | null | undefined>;
  tariffStore: TariffStore;
  bookingStore: BookingStore;
  rootStore: RootStore;
  currentTariffPriceRef: Ref<string | number>;
  t: (key: string) => string;
  closeToast: () => void;
  wait?: number;
};

type CreatePriceCheckAndUpdateParams = {
  bookingStore: BookingStore;
  tariffStore: TariffStore;
  searchStore: SearchStore;
  rootStore: RootStore;
  tariffRef: Ref<Tariff>;
  appNameRef: Ref<string>;
  selectedAddonsRef: Ref<string[]>;
  policyHolderRef: Ref<DynamicHash>;
  searchFiltersRef: Ref<SearchFilterRow[]>;
  currentTariffPriceRef: Ref<string | number>;
  visibleBookingFieldsRef: Ref<VisibleBookingFieldsMap>;
  insuredEntityFormRef: Ref<BookingFieldGroup[]>;
  tariffBookingFormRef: Ref<BookingFieldGroup[]>;
  policyHolderBookingFormRef: Ref<BookingFieldGroup[]>;
  insuredEntityRef: Ref<Record<string, DynamicHash[]>>;
  isLastPageRef: Ref<boolean>;
  updateTariffPrice: typeof updateTariffPrice;
  updatePriceCallBack: UpdatePriceCallBackFn;
  wait?: number;
};

type CollectionTooltipValue = {
  id?: unknown;
  label?: unknown;
  tooltip?: unknown;
};

/** Fields from current tariff for POST /api/v1/bookings payload entries. */
function tariffBookingEndpointFlags(tariff: Tariff | null | undefined) {
  if (!tariff) return {};
  return {
    ...(tariff.contract_model !== undefined && {
      contract_model: tariff.contract_model,
    }),
    ...(tariff.subject_to_inquiry !== undefined && {
      subject_to_inquiry: tariff.subject_to_inquiry,
    }),
  };
}

type BrokerAffiliateContractSlice = {
  brokerEmail?: string;
  poolEmail?: string;
  brokerName?: string;
  brokerPhone?: string;
  brokerCompany?: string;
  poolName?: string;
  brokerId?: string;
  brokerSubId?: string;
};

/**
 * Sets broker / pool fields on `affiliate` for POST /api/v1/bookings.
 * Uses Pinia values first, then sessionStorage (URL merge), same as booking session payload.
 */

function applyBrokerAffiliateFields(
  affiliate: Record<string, unknown>,
  contractData: BrokerAffiliateContractSlice,
) {
  const ss = (key: string): string => {
    try {
      const v = sessionStorage.getItem(key);
      return typeof v === "string" ? v : "";
    } catch (e) {
      rollbar?.error(e as Error, {
        context: "utils:applyBrokerAffiliateFields.ss",
      });
      return "";
    }
  };
  const coalesce = (
    rootVal: string | undefined,
    sessionKey: string,
  ): string => {
    const r = typeof rootVal === "string" ? rootVal.trim() : "";
    if (r !== "") return r;
    return ss(sessionKey);
  };

  const brokerEmailVal = coalesce(contractData.brokerEmail, "broker_email");
  if (brokerEmailVal) affiliate.broker_email = brokerEmailVal;

  const poolEmailVal = coalesce(contractData.poolEmail, "pool_email");
  if (poolEmailVal) affiliate.pool_email = poolEmailVal;

  const brokerNameVal = coalesce(contractData.brokerName, "broker_name");
  if (brokerNameVal) affiliate.broker_name = brokerNameVal;

  const brokerPhoneVal = coalesce(contractData.brokerPhone, "broker_phone");
  if (brokerPhoneVal) affiliate.broker_phone = brokerPhoneVal;

  const brokerCompanyVal = coalesce(
    contractData.brokerCompany,
    "broker_company",
  );
  if (brokerCompanyVal) affiliate.broker_company = brokerCompanyVal;

  const poolNameVal = coalesce(contractData.poolName, "pool_name");
  if (poolNameVal) affiliate.pool_name = poolNameVal;

  const brokerIdVal = coalesce(contractData.brokerId, "broker_id");
  if (brokerIdVal) affiliate.broker_id = brokerIdVal;

  const brokerSubIdVal = coalesce(contractData.brokerSubId, "broker_sub_id");
  if (brokerSubIdVal) affiliate.broker_sub_id = brokerSubIdVal;
}

function addSearchFilterTooltips(contractData: {
  searchFilters: SearchFilterRow[];
  searchForm?: SearchField[];
}) {
  const searchFilters = contractData.searchFilters;
  const searchForm = contractData.searchForm || [];

  if (!Array.isArray(searchFilters) || !Array.isArray(searchForm)) {
    return searchFilters;
  }

  return searchFilters.map((filter) => {
    if (filter.field_type !== "collection") {
      return filter;
    }

    if (!filter.search_field_id) {
      return filter;
    }

    const searchField = searchForm.find(
      (field) => String(field?.id ?? "") === String(filter.search_field_id),
    );

    if (!searchField) {
      return filter;
    }

    const searchFieldValues = searchField?.attributes?.search_field_values;

    if (!Array.isArray(searchFieldValues) || searchFieldValues.length === 0) {
      return filter;
    }

    const selectedValues = Array.isArray(filter.value)
      ? filter.value
      : [filter.value];

    const selectedTooltips = selectedValues.map((selectedValue) => {
      const option = searchFieldValues.find(
        (searchFieldValue) =>
          String(searchFieldValue?.id ?? "") === String(selectedValue ?? ""),
      );
      return option?.tooltip ?? "";
    });

    return {
      ...filter,
      tooltip: Array.isArray(filter.value)
        ? selectedTooltips
        : selectedTooltips[0],
    };
  });
}

function applyVerificationAffiliateFields(
  affiliate: Record<string, unknown>,
  contractData: {
    bookingData: {
      policy_holder: Record<string, unknown>;
      visibleBookingFields: VisibleBookingFieldsMap;
      verificationLevel?: unknown;
      bookingForms: BookingFieldGroup[];
    };
  },
): void {
  const policyHolder = contractData.bookingData.policy_holder;
  const visibleFields = (contractData.bookingData.visibleBookingFields
    .policy_holder ?? {}) as Record<
    string,
    { isBookingVisible?: boolean } | undefined
  >;

  if (isEmailVerificationEnforced()) {
    enforceEmailVerificationInPolicyHolder(policyHolder);
    if (contractData.bookingData.verificationLevel !== null) {
      affiliate.verificationLevel = contractData.bookingData.verificationLevel;
    }
    affiliate.verification_not_required = false;
    return;
  }

  if (
    policyHolder.verification_not_required !== undefined &&
    visibleFields.verification_not_required !== undefined &&
    visibleFields.verification_not_required.isBookingVisible === true
  ) {
    if (policyHolder.verification_not_required === "") {
      for (const index in contractData.bookingData.bookingForms) {
        if (
          contractData.bookingData.bookingForms[index]
            .booking_field_group_type === "policy_holder"
        ) {
          for (const field in contractData.bookingData.bookingForms[index]
            .app_booking_fields) {
            if (
              contractData.bookingData.bookingForms[index].app_booking_fields[
                field
              ].field_name === "verification_not_required"
            ) {
              policyHolder.verification_not_required =
                contractData.bookingData.bookingForms[index].app_booking_fields[
                  field
                ].default_value;
              break;
            }
          }
        }
      }
    }

    if (contractData.bookingData.verificationLevel !== null) {
      affiliate.verificationLevel = contractData.bookingData.verificationLevel;
    }
    affiliate.verification_not_required =
      policyHolder.verification_not_required;
  } else if (contractData.bookingData.verificationLevel !== null) {
    affiliate.verificationLevel = contractData.bookingData.verificationLevel;
  }
}

const utils = {
  createMetaData,
  createMetaDataLink,
  applyLanguage,
  applyThemeClasses,
  affiliateIdToUse,
  decodeBase64,
  isBase64,
  isBase64EncodedAffiliatePayload,
  comparisonValueTitle,
  decodeHtmlEntities,
  isHtmlEmpty,
  collectionLabelsEqual,
  findSingleExactCollectionOptionByLabel,
  isResolvedCollectionValue,
  normalizeCountryCollectionValue,
  sortDropDownOptions,
  asyncfocusDatePicker,
  asyncfocusMultiselect,
  resetMultiselectDropdown,
  resizeMultiselectDropdown,
  hasCookie,
  getCookie,
  getCookieValue,
  setCookie,
  sessionStorageGet,
  sessionStorageSet,
  checkForRelativeDates,
  formatDate,
  getDefaultDate,
  iFrameResize,
  iFrameResizeNow,
  iFrameResizeToPixels,
  postMessageWrapper,
  calculateLegalAge,
  isValidDate,
  isValidDateFormat,
  setValidDate,
  swapDateFormats,
  getPriceChangeState,
  getSanitizedNumber,
  normalizeNumberString,
  normalizeNumberStringForUrl,
  parsePriceToNumber,
  tariffPrice,
  withDotDecimalsForNumberFields,
  priceFormat,
  getAlertClass(type: string) {
    const map = {
      error: "alert alert-danger",
      warning: " alert alert-warning",
      info: "alert alert-info",
      success: "alert alert-success",
      light: "alert alert-light",
      dark: "alert alert-dark",
    };
    return map[type] || "";
  },
  shouldShowParticipantSavingInterface(activeUser: unknown) {
    return Boolean(String(activeUser || "").trim());
  },
  shuffle<T>(arr: T[]): T[] {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  },
  convertLabel(
    searchStore: SearchStore,
    filter: {
      search_field_id?: string | number;
      value?: unknown;
      display_value?: string;
    },
  ): string {
    const searchFields = Object.values(
      searchStore.searchFields,
    ) as SearchField[];
    const searchField = searchFields.find(
      (field) => field.id === filter.search_field_id,
    );

    if (searchField) {
      const attributes =
        searchField.attributes as SearchFieldAttributesWithFormatting;
      const precision = attributes.precision;
      const thousandSeparator = attributes.thousand_separator;
      const unit = attributes.unit; // may be undefined
      const rawStr = filter.value == null ? "" : String(filter.value);

      const numericLabel = utils.getSanitizedNumber(
        rawStr,
        precision,
        thousandSeparator,
      );

      return unit ? `${numericLabel} ${unit}` : numericLabel;
    }
    return String(filter.value ?? filter.display_value ?? "");
  },
  isEmptyBookingFieldValue(value: unknown): boolean {
    return value === "" || value === undefined || value === null;
  },

  getSessionBookingFieldValue(
    bookingFields: Record<string, BookingFormSlice> | undefined,
    formName: string,
    field: BookingField,
  ) {
    if (!formName.startsWith("tariff") || field.field_type !== "date") {
      return undefined;
    }

    const sessionFormFields = bookingFields?.[formName];

    if (!sessionFormFields || typeof sessionFormFields !== "object") {
      return undefined;
    }

    const fieldNameValue = sessionFormFields[field.field_name];
    if (!utils.isEmptyBookingFieldValue(fieldNameValue)) {
      return fieldNameValue;
    }

    return undefined;
  },
  checkIfEmptyValue(input: unknown): boolean {
    const isEmpty = (v: unknown): boolean => {
      if (v === null || v === undefined) return true;
      if (Array.isArray(v)) return v.length === 0;
      if (typeof v === "string") return v.trim() === "";
      if (typeof v === "object") {
        const keys = Object.keys(v);
        if (keys.length === 0) return true;
        if ("id" in v) {
          const idVal = (v as { id?: unknown }).id;
          return (
            idVal === null || idVal === undefined || String(idVal).trim() === ""
          );
        }

        return false;
      }
      return false;
    };
    if (Array.isArray(input)) {
      for (const item of input) {
        if (isEmpty(item)) return false;
        const val =
          item && typeof item === "object" && "value" in item
            ? item.value
            : item;
        if (isEmpty(val)) return false;
      }
      return true;
    }
    if (input && typeof input === "object") {
      for (const key in input) {
        if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
        const v = input[key];
        if (isEmpty(v)) return false;
      }
      return true;
    }
    return false;
  },
  addFilterToQuery(
    query,
    tariffs: SelectedFilters,
    payment,
    possibleIntervals,
    rootStore,
    campaign,
    contractRenewal,
  ) {
    const tariffsData = Object.values(tariffs);
    const zahlweiseRegex = new RegExp(`&zahlweise=([^&]*)`);
    const paymentFieldRegex = new RegExp(`&payment_interval_filter=([^&]*)`);
    const groupedTariffs: Record<string, (string | number)[]> = {};
    tariffsData.forEach((tariff) => {
      if (!groupedTariffs[tariff.type]) {
        groupedTariffs[tariff.type] = [];
      }
      groupedTariffs[tariff.type].push(tariff.id);
    });
    for (const [_type, ids] of Object.entries(groupedTariffs)) {
      const idsString = ids.join(",");
      const regex = new RegExp(`&gesellschaften=([^&]*)`);
      if (query.match(regex)) {
        query = query.replace(regex, `&gesellschaften=${idsString}`);
      } else {
        query += `&gesellschaften=${idsString}`;
      }
    }
    if (possibleIntervals !== undefined) {
      for (const interval in possibleIntervals) {
        if (
          payment.value &&
          payment.value.toString() === possibleIntervals[interval].id.toString()
        ) {
          if (query.match(zahlweiseRegex)) {
            query = query.replace(
              zahlweiseRegex,
              `&zahlweise=${possibleIntervals[interval].label}`,
            );
          } else {
            query += `&zahlweise=${possibleIntervals[interval].label}`;
          }
        }
      }
    }
    if (query.match(paymentFieldRegex)) {
      query = query.replace(paymentFieldRegex, "");
    }
    if (campaign !== "" && campaign !== undefined) {
      const campaignRegex = new RegExp(`&campaign=([^&]*)`);
      if (!query.match(campaignRegex)) {
        query += `&campaign=${encodeURIComponent(campaign)}`;
      }
    }
    if (contractRenewal !== "" && contractRenewal !== undefined) {
      const campaignRegex = new RegExp(`&vertrag_erneuern=([^&]*)`);
      if (!query.match(campaignRegex)) {
        query += `&vertrag_erneuern=${encodeURIComponent(contractRenewal)}`;
      }
    }
    const paramsToCheck = ["code", "theme", "origin"];
    paramsToCheck.forEach((param) => {
      if (rootStore[param] !== "" && rootStore[param] !== undefined) {
        const paramRegex = new RegExp(`&${param}=([^&]*)`);
        if (!query.match(paramRegex)) {
          query += `&${param}=${encodeURIComponent(rootStore[param])}`;
        }
      }
    });

    if (typeof window !== "undefined") {
      const tmEditRaw = new URLSearchParams(window.location.search).get(
        "tm_edit",
      );
      if (
        tmEditRaw &&
        ["1", "true", "yes", "on"].includes(tmEditRaw.trim().toLowerCase())
      ) {
        const tmEditRegex = /&tm_edit=([^&]*)/;
        if (!query.match(tmEditRegex)) {
          query += `&tm_edit=1`;
        }
      }
    }

    const brokerParams = [
      "broker_info_prefill",
      "broker_phone",
      "broker_name",
      "broker_company",
      "broker_info_visible",
      "broker_email",
      "pool_email",
      "pool_name",
      "broker_id",
      "broker_sub_id",
    ];
    brokerParams.forEach((key) => {
      const value = sessionStorageGet(key);
      if (value && value !== "") {
        const paramRegex = new RegExp(`&${key}=([^&]*)`);
        if (!query.match(paramRegex)) {
          query += `&${key}=${encodeURIComponent(value)}`;
        }
      }
    });

    return query;
  },
  alteredFilters() {
    const searchStore = useSearchStore();
    const alteredSearchFilters = searchStore.searchFilters;
    for (const i in alteredSearchFilters) {
      for (const field in searchStore.searchFields) {
        const searchField = searchStore.searchFields[field];
        if (
          alteredSearchFilters[i].search_field_id &&
          searchField.id.toString() ===
            alteredSearchFilters[i].search_field_id.toString()
        )
          alteredSearchFilters[i].field_name =
            searchField.attributes.attribute_name;
      }
    }
    return alteredSearchFilters;
  },
  updatesValuesManager() {
    const searchStore = useSearchStore();
    const updatedValuesManager = {};
    const updatedForm = {};
    const form = JSON.parse(JSON.stringify(searchStore.searchFieldValues));
    for (const key in form) {
      for (const i in searchStore.searchFields) {
        const field = searchStore.searchFields[i];
        if (field.attributes.alias_name === key) {
          let display_value = searchStore.searchFieldValues[key];
          const fieldPossibleValues = field.attributes.search_field_values;
          if (fieldPossibleValues.length !== 0) {
            for (const index in fieldPossibleValues) {
              if (fieldPossibleValues[index].id === display_value) {
                display_value = fieldPossibleValues[index].id;
              }
            }
          }
          updatedValuesManager[field.attributes.attribute_name] = display_value;
          updatedForm[field.attributes.attribute_name] =
            field.attributes.alias_name;
          break;
        }
      }
    }
    searchStore.searchValuesManager = updatedValuesManager;
    searchStore.setSearchMappingFields(updatedForm);
  },
  getSalutation(salutation) {
    if (typeof salutation !== "string") {
      return "";
    }
    const prefix = "translation missing: de.booking.salutation.";
    if (salutation.startsWith(prefix)) {
      return "Herr";
    }
    return salutation;
  },
  handleFieldVisibility(
    field: string,
    bookingFieldGroup:
      Record<string, fieldVisibility> | Record<string, fieldVisibility>[],
    visibility: boolean,
    isInsuredEntity: boolean,
  ) {
    const group = isInsuredEntity
      ? (Object.values(bookingFieldGroup) as Record<string, fieldVisibility>[])
      : [bookingFieldGroup as Record<string, fieldVisibility>];
    group.forEach((subGroup) => {
      if (subGroup[field]) {
        const fieldOccurrences = Object.keys(subGroup).filter(
          (key) => key === field,
        ).length;
        if (fieldOccurrences > 1) {
          console.warn(
            `Multiple fields with the name "${field}" exist. Skipping operation. Replace name with id.`,
          );
          return;
        }
        subGroup[field].isBookingVisible = visibility;
      }
    });
  },
  readCollectionValue(
    formName: string,
    fieldName: string,
    entityIndex: number,
    bookingStore: CollectionValueBookingStore,
  ) {
    if (formName.startsWith("insured_entity")) {
      return (
        bookingStore.$state.insured_entity?.[formName]?.[entityIndex]?.[
          fieldName
        ] || { id: "", label: "" }
      );
    } else if (formName.startsWith("policy_holder")) {
      return (
        bookingStore.$state.policy_holder?.[fieldName] || { id: "", label: "" }
      );
    } else if (formName.startsWith("tariff")) {
      const formSlice = bookingStore.$state[formName] as
        Record<string, unknown> | undefined;
      return (
        (formSlice?.[fieldName] as
          { id: string; label: string } | undefined) || {
          id: "",
          label: "",
        }
      );
    }
    return { id: "", label: "" };
  },
  writeCollectionValue(
    formName: string,
    fieldName: string,
    entityIndex: number,
    value: { id: string; label: string },
    bookingStore: CollectionValueBookingStore,
  ) {
    if (formName.startsWith("insured_entity")) {
      const insuredEntity = bookingStore.$state.insured_entity;
      if (!insuredEntity?.[formName]?.[entityIndex]) return;
      insuredEntity[formName][entityIndex][fieldName] = value;
    } else if (formName.startsWith("policy_holder")) {
      if (!bookingStore.$state.policy_holder) {
        bookingStore.$state.policy_holder = {};
      }
      bookingStore.$state.policy_holder[fieldName] = value;
    } else if (formName.startsWith("tariff")) {
      const stateRecord = bookingStore.$state as Record<
        string,
        BookingFormSlice
      >;
      stateRecord[formName] = stateRecord[formName] || {};
      (stateRecord[formName] as Record<string, unknown>)[fieldName] = value;
    }
  },
  evaluateCondition(
    field: BookingField,
    tariff: Tariff,
    linkOutStore: Record<string, unknown>,
  ) {
    if (typeof field.conditions === "string") {
      try {
        const conditionFunction = new Function(
          "tariff",
          "form",
          `${conditionsForBookingFieldEval(
            field,
          )}; return covomoBookingFieldConfig(tariff, form);`,
        );
        return conditionFunction(tariff, linkOutStore);
      } catch (error) {
        rollbar?.error(error as Error, {
          context: "utils:evaluateCondition",
        });
        return null;
      }
    }
    return null;
  },

  getFlatObjValues(bookingStore: BookingStore, searchStore: SearchStore) {
    const tariffs: Record<string, DynamicHash> = {};
    for (const key in bookingStore.$state) {
      if (key.startsWith("tariff") && /^\d+$/.test(key.slice(6))) {
        bookingStore.bookingForms.forEach((form, _index) => {
          if (form.booking_field_group_type === "tariff") {
            const tariff = form.booking_field_group_type + form.id.toString();
            if (tariff === key) {
              const slug = form.slug;
              tariffs[slug] = JSON.parse(
                JSON.stringify(bookingStore.$state[key]),
              );
            }
          }
        });
      }
    }
    const policyHolder = JSON.parse(JSON.stringify(bookingStore.policy_holder));
    const search = JSON.parse(JSON.stringify(searchStore.searchFieldValues));
    const newSearch = {};
    for (const i in searchStore.searchFields) {
      const field = searchStore.searchFields[i].attributes;
      if (search[field.alias_name]) {
        newSearch[field.attribute_name] = search[field.alias_name];
      }
    }
    let insuredEntityArray: DynamicHash[] | null = null;
    for (const key in bookingStore.insured_entity) {
      if (Array.isArray(bookingStore.insured_entity[key])) {
        insuredEntityArray = bookingStore.insured_entity[key];
        break;
      }
    }
    const insuredEntity = JSON.parse(JSON.stringify(insuredEntityArray));
    const paymentData = JSON.parse(JSON.stringify(bookingStore.payment_method));
    bookingStore.setBookingAndSearchValues(
      policyHolder,
      tariffs,
      insuredEntity,
      newSearch,
      paymentData,
    );
  },
  nextStep(active, completed) {
    useRootStore().activeStep = active + 1;
    useRootStore().completedSteps = completed + 1;
  },
  previousStep(active, completed) {
    useRootStore().activeStep = active - 1;
    useRootStore().completedSteps = completed - 1;
  },
  deleteRParameter() {
    const searchStore = useSearchStore();
    const typeToDelete = "r";
    searchStore.deleteFromVisibleSearchFields(typeToDelete);
    for (const key in searchStore.lastFilters) {
      if (searchStore.lastFilters[key].type === "r") {
        delete searchStore.lastFilters[key];
        break;
      }
    }
  },
  scrollWindowHelper(
    top: number,
    left: number,
    behavior: ScrollBehavior,
  ): void {
    window.scrollTo({
      top: top,
      left: left,
      behavior: behavior,
    });
  },
  isTooltipValid(tooltip: unknown): boolean {
    const letterRegex = /[a-zA-Z]/;
    const check = letterRegex.test(String(tooltip ?? ""));
    if (tooltip && check) {
      return true;
    } else {
      return false;
    }
  },

  createUpdatePriceCallBack({
    router,
    updatePriceEndPoint,
    tariffRef: _tariffRef,
    tariffStore,
    bookingStore,
    rootStore,
    currentTariffPriceRef,
    t: _t,
    closeToast: _closeToast,
    wait = 500,
  }: CreateUpdatePriceCallBackParams) {
    return debounce(async function (appName: string, payload: DynamicHash) {
      try {
        const results = await updatePriceEndPoint(appName, payload, router);
        const firstPrice = results?.data?.prices?.[0]?.[0] as
          Record<string, unknown> | undefined;
        if (firstPrice && typeof firstPrice === "object") {
          // tariffStore and bookingStore hold separate shallow copies of the
          // tariff — keep both in sync when price recalc updates contract flags.
          const tariffsToUpdate = [
            tariffStore.currentTariff,
            bookingStore.currentTariff,
          ].filter((tariff): tariff is Tariff => Boolean(tariff));
          const sti = firstPrice.subject_to_inquiry;
          if (typeof sti === "boolean") {
            for (const tariff of tariffsToUpdate) {
              if (sti !== Boolean(tariff.subject_to_inquiry)) {
                tariff.subject_to_inquiry = sti;
              }
            }
          }
          const por = firstPrice.price_on_request;
          if (typeof por === "boolean") {
            for (const tariff of tariffsToUpdate) {
              if (por !== Boolean(tariff.price_on_request)) {
                tariff.price_on_request = por;
              }
            }
          }
          const paymentOptions = firstPrice.payment_options;
          if (Array.isArray(paymentOptions)) {
            for (const tariff of tariffsToUpdate) {
              tariff.payment_options = paymentOptions;
            }
          }
          const cm = firstPrice.contract_model;
          if (typeof cm === "string") {
            for (const tariff of tariffsToUpdate) {
              if (cm !== tariff.contract_model) {
                tariff.contract_model = cm;
              }
            }
          }
        }
        const totalPrice = results?.data?.total || 0;
        const pricesArray = (results?.data?.prices ?? []).map(
          (row: Record<string, unknown>, index: number) => {
            const cell = row[String(index)] ?? Object.values(row)[0];
            const raw =
              cell && typeof cell === "object"
                ? (cell as Record<string, unknown>).price
                : cell;
            return Number(raw);
          },
        );
        bookingStore.setInsuredEntityPriceArray(pricesArray);
        bookingStore.setNextButtonState(pricesArray.includes(0));
        const previousPrice = Number(currentTariffPriceRef.value);
        const nextPrice = Number(totalPrice);
        const priceActuallyChanged =
          Number.isFinite(previousPrice) &&
          Number.isFinite(nextPrice) &&
          previousPrice !== nextPrice;
        if (priceActuallyChanged) {
          rootStore.setFinalPrice(totalPrice);
          bookingStore.setCurrentTariffPrice(totalPrice);
        }
        // Keep "ab" while the premium still matches the starting estimate.
        // Conditional price fields (e.g. GPS for used bikes) can appear later.
        // Clear "ab" only when recalculation changes the amount (payment interval
        // / page >= 2 still flip tariffPriceChanged elsewhere).
        if (
          priceActuallyChanged &&
          nextPrice > 0 &&
          results.data.prices.length >= 1 &&
          !pricesArray.includes(0)
        ) {
          bookingStore.setTariffPriceChanged(true);
        }
        if (
          results.data.prices.length < 1 ||
          pricesArray.includes(0) ||
          totalPrice === 0
        ) {
          // Unavailable tariff is handled via dedicated modal; no global loading overlay needed.
          rootStore.redirectBooking = false;
          rootStore.setTariffUnavailable(true);
          const error = results?.data?.prices?.[0]?.[0]?.errors?.[0];
          if (error) {
            rootStore.setUnavailableTariffMessage(error);
          }
        } else {
          rootStore.redirectBooking = false;
          rootStore.setTariffUnavailable(false);
          rootStore.setUnavailableTariffMessage("");
        }
        bookingStore.setValidatingBookingDataState(false);
      } catch (e: unknown) {
        if (isRequestCanceled(e)) {
          bookingStore.setValidatingBookingDataState(false);
          return;
        }

        rollbar?.error(e as Error, {
          context: "utils:validateBookingData",
        });
        throw e;
      }
    }, wait);
  },
  createPriceCheckAndUpdate({
    bookingStore,
    tariffStore: _tariffStore,
    searchStore,
    rootStore: _rootStore,
    tariffRef,
    appNameRef,
    selectedAddonsRef,
    policyHolderRef,
    searchFiltersRef,
    currentTariffPriceRef: _currentTariffPriceRef,
    visibleBookingFieldsRef,
    insuredEntityFormRef,
    tariffBookingFormRef,
    policyHolderBookingFormRef,
    insuredEntityRef,
    isLastPageRef,
    updateTariffPrice,
    updatePriceCallBack,
    wait = 300,
  }: CreatePriceCheckAndUpdateParams) {
    const run = function priceCheckAndUpdate() {
      const entityPriceData: EntityPriceDataEntry[] = [];
      let visiblePriceUpdateEntityFields: string[] | undefined;
      const visiblePriceUpdateTariffFields: string[] = [];

      const priceUpdatePolicyHolderFields =
        policyHolderBookingFormRef.value.flatMap((form: BookingFieldGroup) => {
          return form.app_booking_fields.filter((field: BookingField) => {
            return field.price_changeable;
          });
        });

      const priceUpdateTariffFields = tariffBookingFormRef.value.flatMap(
        (form: BookingFieldGroup) => {
          return form.app_booking_fields.filter((field: BookingField) => {
            return field.price_changeable;
          });
        },
      );

      const entityFormPriceFields = insuredEntityFormRef.value.flatMap(
        (form: BookingFieldGroup) => {
          return form.app_booking_fields.filter((field: BookingField) => {
            return field.price_changeable;
          });
        },
      );

      const priceUpdateFieldNamesTariff = priceUpdateTariffFields.map(
        (field: BookingField) => field.field_name,
      );

      const priceUpdateFieldNamesPolicyHolder =
        priceUpdatePolicyHolderFields.map(
          (field: BookingField) => field.field_name,
        );

      const priceUpdateFieldNamesEntity = entityFormPriceFields.map(
        (field: BookingField) => field.field_name,
      );

      insuredEntityFormRef.value.forEach((form: BookingFieldGroup) => {
        const formName = `${form.booking_field_group_type}${form.id}`;
        if (visibleBookingFieldsRef.value[formName]) {
          const visibleFieldNames = (
            visibleBookingFieldsRef.value[formName] as Array<
              Record<string, fieldVisibility>
            >
          ).flatMap((entity) => {
            const entityKeys = Object.keys(entity);
            return entityKeys.filter(
              (key) => entity[key].isBookingVisible === true,
            );
          });
          visiblePriceUpdateEntityFields = visibleFieldNames.filter(
            (visibleName: string) =>
              priceUpdateFieldNamesEntity.includes(visibleName),
          );
          const flattened = insuredEntityRef.value[formName].flatMap(
            (entity) => [entity as DynamicHash],
          );
          const entityFields = flattened.map((entity: DynamicHash) => {
            const keys = Object.keys(entity);
            const filtered: DynamicHash[] = [];
            keys.forEach((fieldName) => {
              if (visiblePriceUpdateEntityFields?.includes(fieldName)) {
                filtered.push({ [fieldName]: entity[fieldName] });
              }
            });
            return filtered.reduce((acc, curr) => {
              return { ...acc, ...curr };
            }, {} as DynamicHash);
          });
          if (entityFields.length) {
            entityPriceData.push({
              form_name: formName,
              fields: entityFields,
            });
          }
        }
      });

      const tariffFieldsPriceData = () => {
        const allPriceData: PriceData[] = [];
        tariffBookingFormRef.value.forEach((form: BookingFieldGroup) => {
          const formName = `${form.booking_field_group_type}${form.id}`;
          if (
            visibleBookingFieldsRef.value[formName] &&
            bookingStore.$state[formName]
          ) {
            const formKeys = Object.keys(
              visibleBookingFieldsRef.value[formName],
            );
            const visibleKeys = formKeys.filter(
              (key) =>
                visibleBookingFieldsRef.value[formName][key]
                  .isBookingVisible === true,
            );
            const visiblePriceFields = visibleKeys.filter((visibleName) => {
              return priceUpdateFieldNamesTariff.includes(visibleName);
            });
            const priceFields = visiblePriceFields.filter((name) => {
              return (
                bookingStore.$state[formName][name] !== " " ||
                bookingStore.$state[formName][name] !== undefined
              );
            });
            if (priceFields.length) {
              priceFields.forEach((field) => {
                allPriceData.push({
                  form: formName,
                  field: field,
                  store: bookingStore.$state,
                });
                visiblePriceUpdateTariffFields.push(field);
              });
            }
          }
        });
        return Array.from(new Set([...allPriceData.flat()]));
      };

      const policyHolderPriceData = () => {
        const formKeys = Object.keys(
          visibleBookingFieldsRef.value["policy_holder"],
        );
        const visibleKeys = formKeys.filter(
          (key) =>
            visibleBookingFieldsRef.value["policy_holder"][key]
              .isBookingVisible === true,
        );
        const visiblePriceFields = visibleKeys.filter((visibleName) => {
          return priceUpdateFieldNamesPolicyHolder.includes(visibleName);
        });
        return visiblePriceFields
          .filter((name) => {
            return (
              policyHolderRef.value[name] !== "" ||
              policyHolderRef.value[name] !== undefined
            );
          })
          .flatMap((fieldName) => [policyHolderRef.value[fieldName]]);
      };
      const priceData = {
        entityPrices: entityPriceData,
        tariffFieldsPrices: tariffFieldsPriceData(),
        policyFieldsPrices: policyHolderPriceData(),
      };

      if (!isLastPageRef.value) {
        bookingStore.setNextButtonState(true);
        bookingStore.setValidatingBookingDataState(true);
        const currentTariff = tariffRef.value as Tariff;
        const extraSearchFilters = searchStore.extraSearchValues;
        if (extraSearchFilters && Object.keys(extraSearchFilters).length > 0) {
          const targetType = "payment_interval_filter";
          const paymentIntervalFilterObject = searchFiltersRef.value.find(
            (obj) => obj.type === targetType,
          );
          if (!paymentIntervalFilterObject) {
            if (extraSearchFilters.type === targetType)
              bookingStore.setPaymentInterval({ id: extraSearchFilters.value });
            searchFiltersRef.value.push(extraSearchFilters);
          }
        }

        for (const field in searchStore.searchFilters) {
          if (searchStore.searchFilters[field].type === "policy_start") {
            if (typeof searchStore.searchFilters[field].value === "object") {
              searchStore.searchFilters[field].value = utils.swapDateFormats(
                searchStore.searchFieldValues[
                  searchStore.searchFilters[field].type
                ],
                "-",
                ".",
              );
            }
          }
        }

        const filteredInsuredEntity: Record<
          string,
          DynamicHash[] | DynamicHash
        > = {};
        for (const formName in insuredEntityRef.value) {
          if (
            Array.isArray(insuredEntityRef.value[formName]) &&
            Array.isArray(visibleBookingFieldsRef.value[formName])
          ) {
            filteredInsuredEntity[formName] = insuredEntityRef.value[
              formName
            ].map((entity: DynamicHash, idx: number) => {
              const visibleEntityFields =
                (
                  visibleBookingFieldsRef.value[formName] as Array<
                    Record<string, fieldVisibility>
                  >
                )[idx] ||
                (
                  visibleBookingFieldsRef.value[formName] as Array<
                    Record<string, fieldVisibility>
                  >
                )[0] ||
                {};
              const filteredFields: DynamicHash = {};
              for (const key of Object.keys(visibleEntityFields)) {
                if (visibleEntityFields[key]?.isBookingVisible === true) {
                  filteredFields[key] = entity?.[key];
                }
              }

              return filteredFields;
            });
          } else {
            filteredInsuredEntity[formName] = insuredEntityRef.value[formName];
          }
        }
        updateTariffPrice(
          appNameRef.value,
          bookingStore,
          visiblePriceUpdateTariffFields,
          priceUpdateFieldNamesPolicyHolder,
          currentTariff.legacy_id,
          searchFiltersRef.value,
          priceData,
          priceUpdateTariffFields,
          priceUpdatePolicyHolderFields,
          entityFormPriceFields,
          filteredInsuredEntity,
          selectedAddonsRef.value,
          updatePriceCallBack,
          bookingStore.resourceTypeOptions,
          searchStore.bookingToken,
        );
      }
    };
    return debounce(run, wait);
  },
  formatIban(iban: string) {
    const capitalsOnly = iban.toUpperCase();
    return capitalsOnly.replace(/ /g, "");
  },
  queryBuilder(values: QueryValueItem[]) {
    const queryValues: Record<string, unknown> = {};
    for (let i = 0; i < values.length; i++) {
      const item = values[i];
      const rawValue = item.value;
      if (Array.isArray(rawValue)) {
        const serialized = rawValue.toString();
        item.value = serialized;
        queryValues[item.type] = serialized;
        continue;
      }
      if (rawValue instanceof Date) {
        const formatted = this.formatDate(rawValue, "en");
        item.value = formatted;
        queryValues[item.type] = formatted;
        continue;
      }
      queryValues[item.type] = rawValue;
    }
    return queryValues;
  },
  convertBookingFieldToSearchFilter(bookingField, val) {
    let value = val;

    if (typeof value === "object" && bookingField.field_type === "collection") {
      value = value.id;
    }
    if (value === null && bookingField.field_type === "boolean") {
      value = false;
    }
    if (
      bookingField.field_type === "collection" &&
      bookingField.resource_type_id !== null
    ) {
      return {
        field_type: bookingField.field_type,
        name: bookingField.name_de,
        position: bookingField.position,
        type: bookingField.field_name,
        booking_field_id: bookingField.id,
        value,
      };
    } else {
      return {
        field_type: bookingField.field_type,
        name: bookingField.name_de,
        position: bookingField.position,
        type: bookingField.field_name,
        value,
      };
    }
  },

  buildQueryString(data, url) {
    if (url !== "" && url !== undefined) {
      const urlWithoutQuestionMark = url.startsWith("?")
        ? url.substring(1)
        : url;
      const searchParams = new URLSearchParams(urlWithoutQuestionMark);
      searchParams.forEach((value, key) => {
        if (
          Object.prototype.hasOwnProperty.call(data, key) &&
          data[key] !== value
        ) {
          data[key] = value;
        }
      });
    }
    let query = "";
    if (Array.isArray(data)) {
      const types = data.map((obj) => obj.type);
      types.forEach((type, index) => {
        query += `&${type}=${data[index].value}`;
      });
      return query;
    }
    const searchKeys = Object.keys(data || {});
    searchKeys.forEach((key) => {
      query += `&${key}=${data[key]}`;
    });
    return query;
  },
  filterMissingArrayObject(
    comparedTariffs: Tariff[],
    tariffResults: Tariff[],
    objectProp: string,
  ) {
    const tariffResultsIds = tariffResults.map((tariff) => tariff[objectProp]);
    return comparedTariffs.filter((tariff: Tariff) => {
      return tariffResultsIds.includes(tariff[objectProp]);
    });
  },
  /**
   * Constructs the marketing object for contract service
   * @function constructMarketing
   * @param {object} stateData - The store
   * @return object
   */
  constructMarketing(stateData) {
    const forms = stateData.sortedBookingForms || stateData.bookingForms || [];
    const $state = (stateData.$state ?? stateData) as unknown as Record<
      string,
      unknown
    >;
    return {
      covomo: readApplyPageFieldBoolean(
        forms,
        3,
        $state,
        "promotional_contact",
      ),
      affiliate: stateData.acceptMarketingAffiliate ?? false,
    };
  },

  createBookingFieldsObject(
    state: BookingFieldsContractState,
    entityIndex: number | undefined,
  ) {
    const visibleBookingFields = state.visibleBookingFields;
    const generalForms = state.bookingData.bookingForms.filter(
      (form) =>
        form.booking_field_group_type !== "insured_entity" &&
        form.app_booking_fields.length,
    );
    const entityForms = state.bookingData.bookingForms.filter(
      (form: BookingFieldGroup) => {
        return (
          form.booking_field_group_type === "insured_entity" &&
          form.app_booking_fields.length
        );
      },
    );
    const bookingFieldsByForm: Record<string, unknown> = {};
    const entitiesPerForm: Record<string, unknown>[][] = [];
    const formsIterated: string[] = [];
    const addCollectionTooltip = (field: BookingField, value: unknown) => {
      if (
        field.field_type !== "collection" ||
        field.resource_type_id !== null ||
        field.multi_select !== false ||
        !value ||
        typeof value !== "object"
      ) {
        return value;
      }

      const collectionValue = value as CollectionTooltipValue;
      const option = (
        field.booking_field_values as CollectionFieldValue[] | undefined
      )?.find(
        (bookingFieldValue) =>
          String(bookingFieldValue?.id ?? "") ===
          String(collectionValue.id ?? ""),
      );

      return {
        ...collectionValue,
        tooltip: option?.tooltip ?? collectionValue.tooltip ?? "",
      };
    };

    generalForms.forEach((form: BookingFieldGroup) => {
      const visibleFieldNames: string[] = [];
      const formName =
        form.booking_field_group_type === "tariff"
          ? form.booking_field_group_type + form.id
          : form.booking_field_group_type;

      const isPaymentMethodForm = formName === "payment_method";
      const isPayerForm = formName === "payer";
      if (
        isPayerForm &&
        !form.white_list_payment_options?.includes(
          state.bookingData.chosenPaymentMethod,
        )
      ) {
        return;
      }
      if (isPaymentMethodForm) {
        if (
          state.bookingData.chosenPaymentMethod !== "insurer_defined" &&
          !form.white_list_payment_options?.includes(
            state.bookingData.chosenPaymentMethod,
          )
        ) {
          return;
        }
        if (
          state.bookingData.chosenPaymentMethod === "insurer_defined" &&
          !form.white_list_payment_options?.includes("sepa")
        ) {
          return;
        }
      }
      const visibilityDataBookingFieldsPerForm = visibleBookingFields[formName];
      for (const key in visibilityDataBookingFieldsPerForm) {
        if (visibilityDataBookingFieldsPerForm[key].isBookingVisible)
          visibleFieldNames.push(key);
      }
      const fields = form.app_booking_fields.filter((f) => {
        return visibleFieldNames.includes(f.field_name);
      });
      const stateObjKeys = Object.keys(state.bookingData.$state[formName]);
      const formFieldsObj: Record<string, unknown> = {};
      const processFields = (filteredFields: BookingField[]) => {
        stateObjKeys.forEach((key) => {
          const field = filteredFields.find((f) => f.field_name === key);
          if (field && !formsIterated.includes(formName)) {
            let value: unknown = state.bookingData.$state[formName][key];
            if (
              value &&
              typeof value === "object" &&
              field.field_type !== "file"
            ) {
              const valueRecord = value as CollectionTooltipValue;
              const hasId = Object.prototype.hasOwnProperty.call(value, "id");
              const hasLabel = Object.prototype.hasOwnProperty.call(
                value,
                "label",
              );

              if (!hasId && !hasLabel && Object.keys(value).length === 0) {
                value = { id: "", label: "" };
              } else {
                const id =
                  hasId && valueRecord.id != null ? valueRecord.id : "";
                const label =
                  hasLabel && valueRecord.label != null
                    ? valueRecord.label
                    : "";
                value = addCollectionTooltip(field, { id, label });
              }
            }
            const sessionValue = utils.getSessionBookingFieldValue(
              state.bookingData.$state.bookingSession?.booking_fields,
              formName,
              field,
            );
            formFieldsObj[field.id] = utils.isEmptyBookingFieldValue(value)
              ? (sessionValue ?? value)
              : value;
          }
        });
      };
      if (
        state.bookingData.$state.payment_method.alternative_payer !==
          undefined &&
        state.bookingData.$state.payment_method.alternative_payer === false &&
        formName === "payer"
      ) {
        const policyHolderNames: string[] = [];
        const visibilityDataPolicyHolder =
          visibleBookingFields["policy_holder"];
        for (const key in visibilityDataPolicyHolder) {
          if (visibilityDataPolicyHolder[key].isBookingVisible)
            policyHolderNames.push(key);
        }
        const filteredFields = form.app_booking_fields.filter((f) => {
          return policyHolderNames.includes(f.field_name);
        });
        processFields(filteredFields);
        bookingFieldsByForm[formName] = {
          ...formFieldsObj,
        };
        formsIterated.push(formName);
      } else {
        processFields(fields);
      }
      // For the active payment_method is a fake booking field
      if (
        formName === "payment_method" &&
        form.white_list_payment_options?.includes(
          state.bookingData.chosenPaymentMethod,
        )
      ) {
        formFieldsObj["payment_type"] = state.bookingData.chosenPaymentMethod;
      }

      bookingFieldsByForm[formName] = {
        ...formFieldsObj,
      };
      formsIterated.push(formName);
    });
    // Ensure payment_method form exists with the chosen payment method
    const hasPaymentMethodForm =
      !!bookingFieldsByForm.payment_method &&
      Object.keys(bookingFieldsByForm.payment_method as Record<string, unknown>)
        .length > 0;
    if (!hasPaymentMethodForm && state.bookingData.chosenPaymentMethod) {
      bookingFieldsByForm.payment_method = {
        payment_type: state.bookingData.chosenPaymentMethod,
      };
    }
    entityForms.forEach((form: BookingFieldGroup) => {
      const formName = form.booking_field_group_type + form.id;
      const visibleEntityFieldNames = getVisibleInsuredEntityFieldNames(
        visibleBookingFields,
        formName,
      );
      if (visibleEntityFieldNames.length === 0) {
        return;
      }

      const entitySource = state.bookingData.$state.insured_entity[formName];
      if (!Array.isArray(entitySource)) {
        return;
      }

      const fields = form.app_booking_fields;
      const formattedEntities = entitySource
        .map((entity: DynamicHash, index: number) => {
          const formattedEntity: Record<string, unknown> = {};
          for (let j = 0; j < fields.length; j++) {
            const existsVisibleForm =
              visibleBookingFields[form.booking_field_group_type + form.id];
            const existsVisisbleFormIndex =
              existsVisibleForm[index] || existsVisibleForm[0];
            if (existsVisisbleFormIndex) {
              const existsField = existsVisisbleFormIndex[fields[j].field_name];
              if (existsField && existsField.isBookingVisible) {
                formattedEntity[fields[j].id] = addCollectionTooltip(
                  fields[j],
                  state.bookingData.$state.insured_entity[
                    form.booking_field_group_type + form.id
                  ][index][fields[j].field_name],
                );
                if (
                  fields[j].field_type === "collection" &&
                  fields[j].resource_type_id === null &&
                  fields[j].multi_select === false
                ) {
                  const entityKey = form.booking_field_group_type + form.id;
                  const v = state.bookingData.$state.insured_entity[entityKey][
                    index
                  ][fields[j].field_name] as unknown;
                  const collectionValue =
                    v && typeof v === "object"
                      ? (v as CollectionTooltipValue)
                      : null;

                  if (
                    collectionValue?.id == null ||
                    (collectionValue &&
                      typeof collectionValue === "object" &&
                      Object.keys(collectionValue).length === 0)
                  ) {
                    formattedEntity[fields[j].id] = addCollectionTooltip(
                      fields[j],
                      { id: "", label: "" },
                    );
                  }
                }
              }
            }
          }
          return formattedEntity;
        })
        .filter(
          (entity: Record<string, unknown>) =>
            Object.keys(entity).length > 0 &&
            !isEmptyInsuredEntityRecord(entity),
        );
      if (formattedEntities.length === 0) {
        return;
      }
      if (entityIndex !== undefined) {
        entitiesPerForm.push(
          formattedEntities.filter(
            (_entity: Record<string, unknown>, idx: number) =>
              idx === entityIndex,
          ),
        );
      } else {
        entitiesPerForm.push(formattedEntities);
      }
    });
    bookingFieldsByForm.insured_entities = entitiesPerForm.flat();
    return bookingFieldsByForm;
  },

  createBookingFieldsObjectLta(state: BookingFieldsContractState) {
    const visibleBookingFields = state.visibleBookingFields;
    const addCollectionTooltip = (field: BookingField, value: unknown) => {
      if (
        field.field_type !== "collection" ||
        field.resource_type_id !== null ||
        field.multi_select !== false ||
        !value ||
        typeof value !== "object"
      ) {
        return value;
      }

      const collectionValue = value as CollectionTooltipValue;
      const option = (
        field.booking_field_values as CollectionFieldValue[] | undefined
      )?.find(
        (bookingFieldValue) =>
          String(bookingFieldValue?.id ?? "") ===
          String(collectionValue.id ?? ""),
      );

      return {
        ...collectionValue,
        tooltip: option?.tooltip ?? collectionValue.tooltip ?? "",
      };
    };

    const generalForms = state.bookingData.bookingForms.filter(
      (form) =>
        form.booking_field_group_type !== "insured_entity" &&
        form.app_booking_fields.length,
    );
    const entityForms = state.bookingData.bookingForms.filter(
      (form: BookingFieldGroup) => {
        return (
          form.booking_field_group_type === "insured_entity" &&
          form.app_booking_fields.length
        );
      },
    );
    const entitiesPerForm: Record<string, unknown>[] = [];
    const entitiesIndex: string[] = [];
    for (const i in entityForms) {
      const entityForm =
        entityForms[i].booking_field_group_type + entityForms[i].id;
      entitiesIndex.push(entityForm);
    }
    for (const entityForm in entitiesIndex) {
      const formId = parseInt(
        entitiesIndex[entityForm].match(/\d+/)?.[0] ?? "0",
        10,
      );
      const form =
        state.bookingData.$state.insured_entity[entitiesIndex[entityForm]];
      for (const entity in form) {
        const formattedEntity: Record<string, unknown> = { form_id: formId };
        const fields = form[entity];
        for (const fieldName in fields) {
          const fieldValue = fields[fieldName] as unknown;
          const collectionFieldValue =
            fieldValue && typeof fieldValue === "object"
              ? (fieldValue as CollectionTooltipValue)
              : null;
          for (const i in entityForms) {
            const entityFormName =
              entityForms[i].booking_field_group_type + entityForms[i].id;
            if (entityFormName === entitiesIndex[entityForm]) {
              for (const field in entityForms[i].app_booking_fields) {
                const currentField = entityForms[i].app_booking_fields[field];
                if (currentField.field_name === fieldName) {
                  if (
                    currentField.field_type === "collection" &&
                    currentField.resource_type_id === null &&
                    currentField.multi_select === false &&
                    (collectionFieldValue?.id == null ||
                      (collectionFieldValue &&
                        typeof collectionFieldValue === "object" &&
                        Object.keys(collectionFieldValue).length === 0))
                  ) {
                    formattedEntity[
                      entityForms[i].app_booking_fields[field].id
                    ] = addCollectionTooltip(currentField, {
                      id: "",
                      label: "",
                    });
                  } else {
                    formattedEntity[
                      entityForms[i].app_booking_fields[field].id
                    ] = addCollectionTooltip(currentField, fieldValue);
                  }
                }
              }
            }
          }
        }
        entitiesPerForm.push(formattedEntity);
      }
    }
    const bookingFieldsByForm: Record<string, unknown> = {};
    const formsIterated: string[] = [];
    generalForms.forEach((form: BookingFieldGroup) => {
      const visibleFieldNames: string[] = [];
      const formName =
        form.booking_field_group_type === "tariff"
          ? form.booking_field_group_type + form.id
          : form.booking_field_group_type;

      const isPaymentMethodForm = formName === "payment_method";
      const isPayerForm = formName === "payer";
      if (
        isPayerForm &&
        !form.white_list_payment_options?.includes(
          state.bookingData.chosenPaymentMethod,
        )
      ) {
        return;
      }
      if (isPaymentMethodForm) {
        if (
          state.bookingData.chosenPaymentMethod !== "insurer_defined" &&
          !form.white_list_payment_options?.includes(
            state.bookingData.chosenPaymentMethod,
          )
        ) {
          return;
        }
        if (
          state.bookingData.chosenPaymentMethod === "insurer_defined" &&
          !form.white_list_payment_options?.includes("sepa")
        ) {
          return;
        }
      }
      const visibilityDataBookingFieldsPerForm = visibleBookingFields[formName];
      for (const key in visibilityDataBookingFieldsPerForm) {
        if (visibilityDataBookingFieldsPerForm[key].isBookingVisible)
          visibleFieldNames.push(key);
      }
      const fields = form.app_booking_fields.filter((f) => {
        return visibleFieldNames.includes(f.field_name);
      });
      const stateObjKeys = Object.keys(state.bookingData.$state[formName]);
      const formFieldsObj: Record<string, unknown> = {};
      for (let i = 0; i < stateObjKeys.length; i++) {
        for (let j = 0; j < fields.length; j++) {
          if (stateObjKeys[i] === fields[j].field_name) {
            if (!formsIterated.includes(formName)) {
              const value = addCollectionTooltip(
                fields[j],
                state.bookingData.$state[formName][stateObjKeys[i]],
              );
              const sessionValue = utils.getSessionBookingFieldValue(
                state.bookingData.$state.bookingSession?.booking_fields,
                formName,
                fields[j],
              );
              formFieldsObj[fields[j].id] = utils.isEmptyBookingFieldValue(
                value,
              )
                ? (sessionValue ?? value)
                : value;
            }
          }
        }
      }
      // fake booking field for payment method
      if (
        formName === "payment_method" &&
        form.white_list_payment_options?.includes(
          state.bookingData.chosenPaymentMethod,
        )
      ) {
        formFieldsObj["payment_type"] = state.bookingData.chosenPaymentMethod;
      }
      bookingFieldsByForm[formName] = {
        ...formFieldsObj,
      };
      formsIterated.push(formName);
    });
    // Ensure payment_method form exists with the chosen payment method
    const hasPaymentMethodFormLta =
      !!bookingFieldsByForm.payment_method &&
      Object.keys(bookingFieldsByForm.payment_method as Record<string, unknown>)
        .length > 0;
    if (!hasPaymentMethodFormLta && state.bookingData.chosenPaymentMethod) {
      bookingFieldsByForm.payment_method = {
        payment_type: state.bookingData.chosenPaymentMethod,
      };
    }
    bookingFieldsByForm.insured_entities = entitiesPerForm.flat();
    return bookingFieldsByForm;
  },
  /**
   * Builds contract object for each insured entity
   * @async
   * @function createTariffManyContracts
   * @param {object} contractData - The object holding all the data required
   * to create the contract object
   * @param entityIndex
   * @return {Object} contract
   */
  async createTariffManyContracts(
    contractData: TariffContractData,
    entityIndex = 0,
  ) {
    const affiliate: Record<string, unknown> = {
      ...contractData.affiliateData,
      code: contractData.code,
      price_token: contractData.priceToken,
    };
    if (contractData.campaign !== " ") {
      affiliate.campaign = contractData.campaign;
    }
    applyBrokerAffiliateFields(affiliate, contractData);

    applyVerificationAffiliateFields(affiliate, contractData);
    return {
      completed_by: contractData.completed_by,
      app: contractData.app,
      tariff_id: contractData.tariff.id,
      legacy_tariff_id: contractData.tariff.legacy_id,
      price: contractData.bookingData.insuredEntityPriceArray[entityIndex],
      searchFilters: buildSearchFiltersForContractPayload(
        addSearchFilterTooltips(contractData),
        (contractData.bookingData.selectedAddons || []).filter(
          (a: string) => typeof a === "string" && a.length > 0,
        ),
        contractData.missingFilters.flat(),
      ),
      affiliate,
      booking_fields: {
        ...this.createBookingFieldsObject(
          contractData as BookingFieldsContractState,
          entityIndex,
        ),
      },
      marketing: {
        ...this.constructMarketing(contractData.bookingData),
      },
      payment_interval: contractData.tariff.payment_interval || "",
      insurer_id: contractData.tariff.insurer.slug,
      search_mask: contractData.originalSearchMask,
      ...tariffBookingEndpointFlags(contractData.tariff),
    };
  },
  /**
   * Builds single contract object
   * @async
   * @function createTariffContract
   * @param {object} contractData - The object holding all the data required
   * to create the contract object
   * @return {Object} contract
   */
  async createContractLinkout(contractData: LinkoutContractData) {
    const bookingFieldsValues = contractData.linkoutVisibleFields;
    const transformedObject: Record<string, unknown> = {};
    if (bookingFieldsValues) {
      for (const key in bookingFieldsValues) {
        if (Object.prototype.hasOwnProperty.call(bookingFieldsValues, key)) {
          const field = bookingFieldsValues[key];
          if (
            field.name === "Titel" &&
            String(field.value ?? "").length === 0
          ) {
            transformedObject[field.id] = "";
          } else {
            transformedObject[field.id] = field.value;
          }
        }
      }
    }
    const affiliate: Record<string, unknown> = {
      ...contractData.affiliateData,
      code: contractData.code,
      price_token: contractData.priceToken,
    };

    if (contractData.campaign !== " ") {
      affiliate.campaign = contractData.campaign;
    }
    applyBrokerAffiliateFields(affiliate, contractData);

    if (contractData.tariff.insurer.id === 30) {
      return {
        completed_by: contractData.customer,
        app: contractData.app,
        tariff_id: contractData.tariff.id,
        legacy_tariff_id: contractData.tariff.legacy_id,
        price: contractData.tariff.price,
        searchFilters: contractData.searchFilters,
        affiliate,
        booking_fields: { policy_holder: { ...transformedObject } },
        payment_interval: contractData.tariff.payment_interval || "",
        insurer_id: contractData.tariff.insurer.slug,
        search_mask: contractData.originalSearchMask,
        ...tariffBookingEndpointFlags(contractData.tariff),
      };
    } else {
      return {
        completed_by: contractData.customer,
        app: contractData.app,
        tariff_id: contractData.tariff.id,
        legacy_tariff_id: contractData.tariff.legacy_id,
        price: contractData.tariff.price,
        searchFilters: [...contractData.searchFilters],
        affiliate,
        booking_fields: { policy_holder: { ...transformedObject } },
        payment_interval: contractData.tariff.payment_interval || "",
        insurer_id: contractData.tariff.insurer.slug,
        search_mask: contractData.searchFilters,
        ...tariffBookingEndpointFlags(contractData.tariff),
      };
    }
  },
  async createTariffContract(contractData: TariffContractData) {
    const affiliate: Record<string, unknown> = {
      ...contractData.affiliateData,
      code: contractData.code,
      price_token: contractData.priceToken,
    };

    if (contractData.campaign !== "") {
      affiliate.campaign = contractData.campaign;
    }
    applyBrokerAffiliateFields(affiliate, contractData);

    applyVerificationAffiliateFields(affiliate, contractData);
    if (contractData.tariff.insurer.id === 30) {
      return {
        completed_by: contractData.completed_by,
        app: contractData.app,
        tariff_id: contractData.tariff.id,
        legacy_tariff_id: contractData.tariff.legacy_id,
        price: contractData.bookingData.currentTariffPrice,
        searchFilters: buildSearchFiltersForContractPayload(
          addSearchFilterTooltips(contractData),
          (contractData.bookingData.selectedAddons || []).filter(
            (a: string) => typeof a === "string" && a.length > 0,
          ),
          contractData.missingFilters.flat(),
        ),
        affiliate,
        booking_fields: {
          ...this.createBookingFieldsObjectLta(
            contractData as BookingFieldsContractState,
          ),
        },
        payment_interval: contractData.tariff.payment_interval || "",
        insurer_id: contractData.tariff.insurer.slug,
        search_mask: contractData.originalSearchMask,
        ...tariffBookingEndpointFlags(contractData.tariff),
      };
    } else {
      return {
        completed_by: contractData.completed_by,
        app: contractData.app,
        tariff_id: contractData.tariff.id,
        legacy_tariff_id: contractData.tariff.legacy_id,
        price: contractData.bookingData.currentTariffPrice,
        searchFilters: buildSearchFiltersForContractPayload(
          addSearchFilterTooltips(contractData),
          (contractData.bookingData.selectedAddons || []).filter(
            (a: string) => typeof a === "string" && a.length > 0,
          ),
          contractData.missingFilters.flat(),
        ),
        affiliate,
        booking_fields: {
          ...this.createBookingFieldsObject(
            contractData as BookingFieldsContractState,
            undefined,
          ),
        },
        payment_interval: contractData.tariff.payment_interval || "",
        insurer_id: contractData.tariff.insurer.slug,
        search_mask: contractData.originalSearchMask,
        ...tariffBookingEndpointFlags(contractData.tariff),
      };
    }
  },
};
export default utils;
